/* eslint-disable react-hooks/exhaustive-deps */
import { message } from "antd";
import { Auth } from "aws-amplify";
import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { decrypt } from "Utility/Common/Methods";
import Logo from "@/Assets/Logo/logo.png";
import AuthImage from "@/Assets/auth-image.png";
import { Button } from "@/Components/ui/button";
import { Input } from "@/Components/ui/input";
import { Field, FieldDescription, FieldGroup, FieldLabel } from "@/Components/ui/field";
import { Loader2 } from "lucide-react";

function getLegacyQueryValue(search, key) {
  const marker = `${key}=`;
  if (!search?.includes(marker)) return "";
  return search.split(marker)?.[1]?.split("+")?.[0] || "";
}

function VerifyEmail() {
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [showEmail, setShowEmail] = useState("");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    const qpCode = params.get("code") || getLegacyQueryValue(location.search, "code");
    const qpEmail = params.get("email") || getLegacyQueryValue(location.search, "email");
    const stateEmail = location?.state?.email || "";

    const resolvedEmail = stateEmail || qpEmail;
    setEmail(resolvedEmail);
    setShowEmail(resolvedEmail);

    if (qpCode) {
      setCode(qpCode);
      handleSubmit({ email: resolvedEmail, code: qpCode });
      return;
    }

    if (!resolvedEmail) {
      navigate("/");
    }
  }, [location]);

  const handleSubmit = async (payload) => {
    const submitEmail = (payload?.email || email || "").trim();
    const submitCode = String(payload?.code || code || "").trim();

    if (!submitEmail) {
      message.error("Email is missing. Please sign up again.");
      return;
    }
    if (!submitCode) {
      message.error("Please enter verification code.");
      return;
    }

    const userCredentials = {
      email: location?.state?.email ?? submitEmail,
      password: location?.state?.password,
    };

    const localItem = localStorage.getItem("cache");
    if (localItem && localItem.length > 0) {
      const jsonString = decrypt(localItem, process.env.REACT_APP_SECUTRY_KEY);
      try {
        const credentials = JSON.parse(jsonString);
        userCredentials.password = credentials.password;
        userCredentials.email = credentials.email;
        localStorage.removeItem("cache");
      } catch (error) {
        console.log(error);
      }
    }

    setIsLoading(true);
    try {
      await Auth.confirmSignUp(submitEmail, submitCode);
      message.success("Email verified");

      navigate("/login", {
        state: {
          ...userCredentials,
          directLogin: Boolean(userCredentials?.password),
        },
      });
    } catch (error) {
      message.error(error?.message || "Could not verify email.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    const resendEmail = String(email || "").trim();
    if (!resendEmail) {
      message.error("Email is missing. Please sign up again.");
      return;
    }

    setIsLoading(true);
    try {
      await Auth.resendSignUp(resendEmail);
      message.success("A new confirmation code has been sent.");
    } catch (error) {
      message.error(error?.message || "Could not resend code.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="grid min-h-svh lg:grid-cols-2">
      <div className="flex flex-col gap-4 p-6 md:p-10">
        <div className="flex justify-center gap-2 md:justify-start">
          <Link
            to="/"
            className="flex items-center gap-2 font-semibold"
            aria-label="Inprofile home"
          >
            <img src={Logo} alt="Inprofile" className="h-8 w-8" />
            Inprofile
          </Link>
        </div>
        <div className="flex flex-1 items-center justify-center">
          <div className="w-full max-w-xs">
            <form
              className="flex flex-col gap-4"
              onSubmit={(e) => {
                e.preventDefault();
                handleSubmit({ email, code });
              }}
            >""
              <FieldGroup className={"gap-4"}>
                <div className="flex flex-col items-center gap-1 text-center">
                  <h1 className="text-2xl font-bold">Verify your email</h1>
                  <p className="text-muted-foreground text-sm text-balance">
                    We&apos;ve sent a verification code to {showEmail || "your email"}
                  </p>
                </div>
                <Field>
                  <FieldLabel htmlFor="verification-code">Verification Code</FieldLabel>
                  <Input
                    id="verification-code"
                    type="text"
                    inputMode="numeric"
                    placeholder="Enter 6 digit code"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))}
                    disabled={isLoading}
                    required
                  />
                </Field>
                <Field>
                  <Button type="submit" className="w-full" disabled={isLoading}>
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 size-4 animate-spin" />
                        Verifying...
                      </>
                    ) : (
                      "Verify Email"
                    )}
                  </Button>
                  <FieldDescription className="text-center">
                    Already verified?{" "}
                    <Link
                      to="/login"
                      className="underline underline-offset-4 text-primary"
                    >
                      Sign in
                    </Link>
                  </FieldDescription>
                </Field>
                <Field>
                  <Button
                    type="button"
                    variant="link"
                    className="w-full"
                    onClick={handleResendCode}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 size-4 animate-spin" />
                        Resending...
                      </>
                    ) : (
                      "Resend verification code"
                    )}
                  </Button>
                </Field>
              </FieldGroup>
            </form>
          </div>
        </div>
      </div>
      <div className="bg-[#f2f5f8] relative hidden lg:block">
        <img
          src={AuthImage}
          alt=""
          className="absolute inset-0 h-full w-full object-contain dark:brightness-[0.2] dark:grayscale"
        />
      </div>
    </div>
  );
}

export default VerifyEmail;
